import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  COOKIE_CONSENT_STORAGE_KEY,
  COOKIE_CONSENT_VERSION,
} from "@/routes/legal/version";

/**
 * Non-blocking cookie / browser-storage disclosure banner. Mounted on the
 * candidate flow only (via CandidateRouteLayout), docked as an in-flow,
 * full-width bar at the bottom of the shell so it reserves space and pushes the
 * assessment up rather than floating over it — it can never intercept a
 * candidate action button (fix P8). Informational, not alarming (no destructive
 * accent).
 *
 * Acknowledgement is per-version: bumping COOKIE_CONSENT_VERSION re-prompts.
 * No server round-trip — the only cookie the platform sets is strictly necessary
 * (the auth session for recruiters), which is exempt from consent under the
 * ePrivacy Directive Art. 5(3) carve-out. This banner is transparency
 * scaffolding, not active compliance load.
 */
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
      if (stored !== COOKIE_CONSENT_VERSION) {
        setVisible(true);
      }
    } catch {
      // localStorage may be disabled (private mode, storage quota exceeded,
      // sandboxed iframe). Show the banner anyway — refusing to render
      // because we can't persist dismissal would be worse than re-prompting.
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(
        COOKIE_CONSENT_STORAGE_KEY,
        COOKIE_CONSENT_VERSION,
      );
    } catch {
      // Best-effort. If localStorage is unavailable the banner will reappear
      // on next mount; that's the right failure mode (we never silently
      // assume consent).
    }
    setVisible(false);
  };

  return (
    // Docked, in-flow, full-width bottom bar (NOT a floating corner toast). It is
    // the last flex child of CandidateRouteLayout, so it reserves its own height
    // and pushes the page content — including the sticky Review / SectionReview
    // footers — up. Because it's in the layout flow it can never overlay a
    // candidate action button at any viewport (fix P8). Earlier shapes floated
    // `fixed` over the flow: a bottom-centred card (QA-002) covered the Welcome
    // CTAs, and the follow-up bottom-right corner toast covered the "Submit final"
    // button on Review (its full-width footer has buttons at both edges, so no
    // floating corner is safe below ~1520px / at mobile widths). Inner content is
    // capped to the same centred `max-w-3xl` column as the footers and reuses
    // their `border-t border-border bg-card` language so it reads as a sibling
    // strip.
    <div
      role="alert"
      aria-live="polite"
      // B10 (2026-09-12 browser QA): in-flow is not enough on its own — at 375
      // the `fixed` z-40 CameraPreview painted straight over this strip, and
      // "Got it" sat fully behind an opaque video frame. The tap still worked
      // (the preview is pointer-events-none, so elementFromPoint returns the
      // button) but the candidate could not see what they were tapping.
      // `relative z-[45]` puts the strip above the decorative preview and
      // still below the FullscreenBlockingOverlay (z-50), which must keep
      // covering everything.
      className="animate-cookie-banner relative z-[45] shrink-0 border-t border-border bg-card"
    >
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-2.5">
        <Cookie
          className="h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
        <p className="min-w-0 flex-1 text-xs leading-snug text-foreground">
          We use minimal browser storage to track your progress.{" "}
          <Link
            to="/cookies"
            className="cursor-pointer text-primary underline transition-colors hover:text-primary/80"
          >
            Cookie Policy
          </Link>
        </p>
        <Button
          type="button"
          size="sm"
          onClick={handleDismiss}
          className="h-7 shrink-0 cursor-pointer px-3 text-xs"
        >
          Got it
        </Button>
      </div>
    </div>
  );
}
